import { useCreateGoalSection, useCreateSubGoalAllowed } from 'features/goals';
import { useCreateHabitAllowed } from 'features/tasks';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle16, Repeat, Section, Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useOpenCreateHabitDialog } from 'shared/contexts/habit-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { ID } from 'shared/types/id';

export type ActionPlanContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  onClose?: () => void;
  parentIds: ID[];
  lifeAreaId?: ID | null;
  id?: ID;
};

export const ActionPlanContextMenu: React.FC<ActionPlanContextMenuProps> = ({
  referenceElement,
  location,
  position,
  onClose,
  parentIds,
  lifeAreaId,
  id,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const isCreateHabitAllowed = useCreateHabitAllowed();
  const { allowed: createSubGoalsAllowed } = useCreateSubGoalAllowed();
  const openPremiumDialog = useOpenPremiumDialog();
  const openCreateGoalDialog = useOpenCreateGoalDialog();
  const openCreateHabitDialog = useOpenCreateHabitDialog();
  const openCreateTask = useOpenCreateTask();
  const createSection = useCreateGoalSection(id);

  const close = () => onClose?.();

  const onCreateTask = () => {
    openCreateTask({ goalId: id });
    close();
  };

  const onCreateHabit = () => {
    openCreateHabitDialog({ goalId: id });
    close();
  };

  const openCreateSubGoal = () => {
    openCreateGoalDialog({
      parentIds,
      lifeAreaId,
    });
    close();
  };

  const _openPremiumDialog = () => {
    openPremiumDialog();
    close();
  };

  const onCreateSection = () => {
    createSection();
    close();
  };

  return (
    <PopupMenu
      ref={containerRef}
      referenceElement={referenceElement}
      location={location}
      position={position}
      onOutside={close}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateTask}
            start={<Icon icon={CheckCircle16} />}
          >
            {t('context-menus.goal.action-plan.labels.add-task')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={isCreateHabitAllowed ? onCreateHabit : _openPremiumDialog}
            isPremium={!isCreateHabitAllowed}
            start={<Icon icon={Repeat} />}
          >
            {t('context-menus.goal.action-plan.labels.add-habit')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateSection}
            start={<Icon icon={Section} />}
          >
            {t('context-menus.goal.action-plan.labels.add-section')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={
              createSubGoalsAllowed ? openCreateSubGoal : _openPremiumDialog
            }
            isPremium={!createSubGoalsAllowed}
            start={<Icon icon={Target} />}
          >
            {t('context-menus.goal.action-plan.labels.add-sub-goal')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
